import React, { PureComponent } from 'react';

import { get } from 'lodash-es';
import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import TaskIcon from '@atlaskit/icon/glyph/task';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { RelativeDate } from '@atlassian/bitkit-date';

import { ScreenReadersOnly } from 'src/components/accessibility';
import { BuildStatusConnected } from 'src/components/build-status';
import {
  TableCellMobileHidden,
  TableCellMobileOnly,
} from 'src/components/pageable-table';
import { PrCycleMetrics } from 'src/components/pull-requests-table/cycle-time/api/requests';
import { User } from 'src/components/types';
import Reviewers from 'src/containers/reviewers';
import { UserAvatar } from 'src/containers/user-avatar';
import { UserName } from 'src/containers/user-name';
import prUrls from 'src/redux/pull-request/urls';
import StateLozenge from 'src/sections/repository/sections/pull-request/components/state-lozenge';
import {
  IconWithCountWrapper,
  IconWithCountIconWrapper,
  SubtleLink,
} from 'src/styles';
import { PullRequest as PullRequestShape } from 'src/types/pull-requests-table';
import { publishUiEvent } from 'src/utils/analytics/publish';

import Comments from './comments';
import { CycleTimeStaleLozenge } from './cycle-time';
import { CycleTimeCreatedColumn } from './cycle-time/cycle-time-created-column';
import ItemInfo from './item-info';
import PullRequestRefBranch from './pull-request-ref-branch';
import { messages, buildStatusMessages } from './pull-request-row.i18n';
import * as styles from './pull-request-row.style';
import { PullRequestsTableStateVisibility } from './pull-requests-table-types';
import {
  COL_SPANS_WITH_AGE,
  PullRequestsTableSize,
  REVIEWER_COUNTS,
} from './pull-requests-table.style';

type PullRequestRowProps = {
  currentUser: User | null | undefined;
  intl: InjectedIntl;
  position: number;
  pullRequest: PullRequestShape;
  showAvatar: boolean;
  showState: PullRequestsTableStateVisibility;
  showRepository: boolean;
  isFocused: boolean | null | undefined;
  isLoading: boolean;
  size: PullRequestsTableSize;
  cycleTimeMetrics?: PrCycleMetrics;
  stale: boolean;
};

export const PullRequestRow = injectIntl(
  class PullRequest extends PureComponent<PullRequestRowProps> {
    static defaultProps = {
      /* eslint react/default-props-match-prop-types: "warn" */
      showAvatar: false,
      showRepository: false,
      showState: PullRequestsTableStateVisibility.None,
      isLoading: false,
    };

    handleLinkClick = () => {
      const { pullRequest, position, stale } = this.props;
      const currentUserUuid = get(this.props, 'currentUser.uuid', '');
      const pullRequestAuthorUuid = get(pullRequest, 'author.uuid');

      // Reviewers property might be absent for some reasons
      // therefore we traverse the list of PR participants
      const isPullRequestReviewer = pullRequest.participants.some(
        participant => {
          if (participant.role === 'REVIEWER') {
            const participantUuid = get(participant, 'user.uuid', '');

            return participantUuid === currentUserUuid;
          }

          return false;
        }
      );

      publishUiEvent({
        action: 'clicked',
        actionSubject: 'link',
        actionSubjectId: 'pullRequestRowLink',
        source: 'pullRequestsTable',
        attributes: {
          position,
          isPullRequestReviewer,
          isPullRequestAuthor: currentUserUuid === pullRequestAuthorUuid,
          stale,
        },
      });
    };

    getStatusUrl() {
      const { pullRequest } = this.props;
      const { id } = pullRequest;
      let statusUrl: string | null = null;

      // The source repository may have been deleted
      if (pullRequest.source.repository) {
        const [owner, slug] =
          pullRequest.source.repository.full_name.split('/');
        statusUrl = prUrls.api.v20.statusesFetch(owner, slug, id);
      }

      return statusUrl;
    }

    renderSourceRefTag() {
      const { pullRequest } = this.props;
      const { source, destination } = pullRequest;

      const sourceUuid = get(source, 'repository.uuid');
      const destinationUuid = get(destination, 'repository.uuid');
      const isDifferentRepo =
        !sourceUuid || !destinationUuid || sourceUuid !== destinationUuid;

      return (
        <styles.SourceBranch>
          <styles.Branch>
            <PullRequestRefBranch
              isDifferentRepo={isDifferentRepo}
              pullRequestRepo={source}
            />
          </styles.Branch>
        </styles.SourceBranch>
      );
    }

    renderSummary({ avatarIsShown = true } = {}) {
      const {
        pullRequest,
        showAvatar,
        showState,
        isFocused,
        isLoading,
        size,
        cycleTimeMetrics,
        stale,
      } = this.props;

      const {
        author,
        destination,
        id,
        links,
        title,
        updated_on: lastUpdated,
        created_on: createdOn,
      } = pullRequest;

      // The PR id is only unique within a repository, and some PR lists show
      // PRs across multiple repos (e.g., the Dashboard)
      const prKey = links.self.href;

      // Show the state lozenge based on the showState prop
      // 'All' shows the state lozenge for all PRs in the table
      // 'DraftsOnly' shows the state lozenge for only draft PRs
      // 'None' shows no state lozenge for any PRs in the table
      let showStateLozenge = false;
      switch (showState) {
        case PullRequestsTableStateVisibility.None:
          showStateLozenge = false;
          break;
        case PullRequestsTableStateVisibility.All:
          showStateLozenge = true;
          break;
        case PullRequestsTableStateVisibility.DraftsOnly:
          showStateLozenge = !!pullRequest?.draft;
          break;
        default:
          break;
      }
      return (
        <ItemInfo
          avatar={
            showAvatar && avatarIsShown ? (
              <UserAvatar
                isDisabled={isLoading}
                profileCardPosition="bottom-start"
                user={author}
              />
            ) : null
          }
          byline={
            <FormattedMessage
              {...(this.props.showRepository
                ? messages.summaryWithRepository
                : messages.summaryWithoutCreated)}
              values={{
                author: (
                  <UserName
                    key={`pr-row-${prKey}`}
                    linkComponent={SubtleLink}
                    profileCardPosition="bottom-start"
                    user={author}
                  />
                ),
                date: (
                  <RelativeDate
                    key={`date-key-${prKey}`}
                    date={new Date(lastUpdated)}
                  />
                ),
                created: (
                  <RelativeDate
                    key={`created-key-${prKey}`}
                    date={new Date(createdOn)}
                  />
                ),
                id,
                repository: (
                  <styles.SubtleRepositoryLink
                    key={`repository-link-key-${prKey}`}
                    repository={destination.repository}
                  />
                ),
              }}
            >
              {(...args) => (
                <styles.BylineNoMargin>
                  {args}{' '}
                  <CycleTimeStaleLozenge
                    stale={stale}
                    pullRequest={pullRequest}
                    cycleTimeMetrics={cycleTimeMetrics}
                  />
                </styles.BylineNoMargin>
              )}
            </FormattedMessage>
          }
          header={
            <styles.SummaryHeader>
              {showStateLozenge && (
                <styles.StateLozengeWrapper>
                  <StateLozenge
                    pullRequestState={pullRequest.state}
                    pullRequestDraft={pullRequest.draft || false}
                    pullRequestCreatedOn={pullRequest.created_on}
                    pullRequestClosedOn={pullRequest.closed_on}
                  />
                </styles.StateLozengeWrapper>
              )}
              <Tooltip content={title}>
                <styles.TooltipBoundaries>
                  <styles.HeaderLink
                    qaId="pull-request-row-link"
                    title={pullRequest.title}
                    href={pullRequest.links.html.href}
                    isFocused={!!isFocused}
                    onClick={this.handleLinkClick}
                    disableNativeTooltip
                  >
                    {title}
                  </styles.HeaderLink>
                </styles.TooltipBoundaries>
              </Tooltip>
              <styles.BranchesContainer>
                {size === PullRequestsTableSize.Large &&
                  this.renderSourceRefTag()}
                <styles.Arrow>
                  <ArrowRightIcon label="" size="small" />
                </styles.Arrow>
                <styles.Branch>
                  <PullRequestRefBranch pullRequestRepo={destination} />
                </styles.Branch>
              </styles.BranchesContainer>
            </styles.SummaryHeader>
          }
        />
      );
    }

    renderBuilds() {
      const { pullRequest } = this.props;
      const { status_counts: buildCounts } = pullRequest;
      const statusUrl = this.getStatusUrl();
      const { commit_statuses: commitStatuses = [] } = pullRequest.extra || {};

      if (statusUrl) {
        return (
          <styles.BuildStatusContainer>
            <BuildStatusConnected
              commitStatuses={commitStatuses}
              buildCounts={buildCounts}
              statusUrl={statusUrl}
              buildFailureTitle={buildStatusMessages.buildFailureTitle}
              buildFailureDescription={
                buildStatusMessages.buildFailureDescription
              }
            />
          </styles.BuildStatusContainer>
        );
      }

      return null;
    }

    render() {
      const {
        intl,
        pullRequest,
        isFocused,
        isLoading,
        size,
        cycleTimeMetrics,
        stale,
      } = this.props;
      const { task_count: taskCount } = pullRequest;

      const colSpans = COL_SPANS_WITH_AGE;

      return (
        <styles.TableRow data-qa="pull-request-row" isFocused={!!isFocused}>
          <TableCellMobileHidden colSpan={colSpans[size].summary}>
            {this.renderSummary()}
          </TableCellMobileHidden>
          <TableCellMobileHidden colSpan={colSpans[size].age}>
            <CycleTimeCreatedColumn
              stale={stale}
              pullRequest={pullRequest}
              cycleTimeMetrics={cycleTimeMetrics}
            />
          </TableCellMobileHidden>
          <TableCellMobileHidden colSpan={colSpans[size].activity}>
            <styles.Activity>
              <Comments pullRequest={pullRequest} />
              {!!taskCount && (
                <IconWithCountWrapper>
                  <IconWithCountIconWrapper>
                    <TaskIcon
                      label=""
                      primaryColor={token('color.icon')}
                      size="medium"
                    />
                  </IconWithCountIconWrapper>
                  <span aria-hidden="true">{taskCount}</span>
                  <ScreenReadersOnly>
                    {intl.formatMessage(messages.taskCount, {
                      taskCount,
                    })}
                  </ScreenReadersOnly>
                </IconWithCountWrapper>
              )}
              {!pullRequest.comment_count && !taskCount && 'No activity'}
            </styles.Activity>
          </TableCellMobileHidden>
          <TableCellMobileHidden colSpan={colSpans[size].reviewers}>
            <Reviewers
              isDisabled={isLoading}
              maxCount={REVIEWER_COUNTS[size]}
              pullRequest={pullRequest}
            />
          </TableCellMobileHidden>
          <TableCellMobileHidden colSpan={colSpans[size].builds}>
            {this.renderBuilds()}
          </TableCellMobileHidden>
          <TableCellMobileOnly colSpan={colSpans[size].mobileSummary}>
            {this.renderSummary({ avatarIsShown: false })}
          </TableCellMobileOnly>
          <TableCellMobileOnly colSpan={colSpans[size].mobileBuilds}>
            {this.renderBuilds()}
          </TableCellMobileOnly>
        </styles.TableRow>
      );
    }
  }
);
